// @flow

import { denormalizeJsonApi } from "./denormalizeJsonApi";

/**
 * Options type for status checking functions with retry capabilities
 */
export type StatusCheckOptions = {
  maxRetries?: number,
  onMaxRetriesReached?: (retryCount: number) => void,
  addMessage?: (message: string, type: string) => void
};

/**
 * Default callback function for when max retries are reached
 * @param {number} retryCount - Number of retries attempted
 * @param {Function} addMessage - Function to display messages to user
 * @param {string} operation - Description of the operation that timed out
 */
export const defaultMaxRetriesCallback = (
  retryCount: number,
  addMessage?: (message: string, type: string) => void,
  operation: string = "Verarbeitung"
) => {
  console.error(`${operation} failed after ${retryCount} retries`);
  if (addMessage) {
    addMessage(
      `${operation} dauert länger als erwartet. Bitte versuchen Sie es später erneut.`,
      "failure"
    );
  }
};

/**
 * Generic status polling utility with retry limits and callbacks
 * @param {Function} apiCall - Function that returns a Promise with the API response
 * @param {Object} options - Configuration options
 * @param {string[]} options.continueStatuses - Array of statuses that should continue polling
 * @param {number} options.maxRetries - Maximum number of retries (default: 30)
 * @param {number} options.retryDelay - Delay between retries in ms (default: 1000)
 * @param {Function} options.onMaxRetriesReached - Callback when max retries exceeded
 * @param {Function} options.extractStatus - Function to extract status from API response
 * @param {number} options.currentRetry - Current retry count (internal use)
 * @returns {Promise} - Resolves with final response or rejects on error
 */
export const pollWithRetries = (
  apiCall: () => Promise<any>,
  options: {
    continueStatuses: string[],
    maxRetries?: number,
    retryDelay?: number,
    onMaxRetriesReached?: (retryCount: number) => void,
    extractStatus?: (response: any) => string,
    currentRetry?: number
  }
): Promise<any> => {
  const {
    continueStatuses,
    maxRetries = 30,
    retryDelay = 1000,
    onMaxRetriesReached = (retryCount: number) => {}, // eslint-disable-line no-unused-vars
    extractStatus = res => {
      const normalized = denormalizeJsonApi(res);
      return normalized?.status;
    },
    currentRetry = 0
  } = options;

  return apiCall().then(res => {
    const status = extractStatus(res);

    if (continueStatuses.includes(status)) {
      if (currentRetry >= maxRetries) {
        // Max retries reached - call callback and resolve with current response
        onMaxRetriesReached(currentRetry);
        return Promise.resolve(res);
      }

      // Continue polling with incremented retry count
      return new Promise(resolve => setTimeout(resolve, retryDelay)).then(() =>
        pollWithRetries(apiCall, {
          ...options,
          currentRetry: currentRetry + 1
        })
      );
    }

    // Status indicates completion - return the response
    return Promise.resolve(res);
  });
};
